import { forwardRef, type HTMLAttributes, type ReactNode } from 'react';

/**
 * Badge / Chip / Tag — small visual markers.
 *
 * Variants (semantic):
 *   solid     : filled background + inverse text. Strong emphasis.
 *   soft      : tinted background + same-tone text. Most common.
 *   outline   : transparent + tinted border + tinted text. Subtle.
 *   dot       : 4px circle + tonal label. Status indicators.
 *
 * Tones:
 *   neutral / accent / sage / amber / brick / ink
 *   tier-{micro|standard|major|master|legendary}
 *
 * Sizes: sm (20px) / md (24px default) / lg (28px)
 */
export type BadgeVariant = 'solid' | 'soft' | 'outline' | 'dot';
export type BadgeTone =
  | 'neutral' | 'accent' | 'sage' | 'amber' | 'brick' | 'ink'
  | 'tier-micro' | 'tier-standard' | 'tier-major' | 'tier-master' | 'tier-legendary';

export interface BadgeProps extends HTMLAttributes<HTMLSpanElement> {
  variant?: BadgeVariant;
  tone?: BadgeTone;
  size?: 'sm' | 'md' | 'lg';
  children?: ReactNode;
}

export const Badge = forwardRef<HTMLSpanElement, BadgeProps>(function Badge(
  { variant = 'soft', tone = 'neutral', size = 'md', className, children, ...rest },
  ref,
) {
  const cn = [
    'bq-badge',
    `bq-badge--${variant}`,
    `bq-badge--tone-${tone}`,
    `bq-badge--${size}`,
    className ?? '',
  ].filter(Boolean).join(' ');

  return (
    <span ref={ref} className={cn} {...rest}>
      {variant === 'dot' && <span className="bq-badge__dot" aria-hidden="true" />}
      {children}
    </span>
  );
});
